// Production-specific asset loading error handler
export function setupAssetErrorHandler() {
  if (typeof window === 'undefined') return;

  // Handle CSS preload errors
  window.addEventListener('error', (event) => {
    const target = event.target as HTMLElement;
    
    // Check if it's a link element (CSS preload)
    if (target?.tagName === 'LINK' && target.getAttribute('rel')?.includes('preload')) {
      const href = target.getAttribute('href');
      
      if (href && href.includes('.css')) {
        console.warn(`CSS preload failed for: ${href}, attempting fallback load`);
        event.preventDefault();
        
        // Remove the failed preload link
        target.remove();
        
        // Create a regular stylesheet link as fallback
        const fallbackLink = document.createElement('link');
        fallbackLink.rel = 'stylesheet';
        fallbackLink.href = href;
        fallbackLink.onerror = () => {
          console.error(`Failed to load CSS: ${href}`);
          // Optionally notify user of asset loading issues
          handleAssetLoadError('css', href);
        };
        
        document.head.appendChild(fallbackLink);
      }
    }
    
    // Handle script loading errors
    if (target?.tagName === 'SCRIPT') {
      const src = target.getAttribute('src');
      if (src) {
        console.error(`Script failed to load: ${src}`);
        handleAssetLoadError('script', src);
      }
    }
  }, true);

  // Handle dynamic import errors
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.message?.includes('Failed to fetch dynamically imported module')) {
      console.error('Dynamic module import failed:', event.reason);
      event.preventDefault();
      
      // Extract module path from error
      const match = event.reason.message.match(/module: (.+)$/);
      const modulePath = match?.[1];
      
      handleAssetLoadError('module', modulePath || 'unknown');
    }
  });
}

// Handle asset loading errors gracefully
function handleAssetLoadError(type: 'css' | 'script' | 'module', path: string) {
  // Check if we've already shown an error recently
  const errorKey = `asset-error-${type}-${path}`;
  const lastError = sessionStorage.getItem(errorKey);
  const now = Date.now();
  
  if (lastError && now - parseInt(lastError) < 5000) {
    return; // Don't spam errors
  }
  
  sessionStorage.setItem(errorKey, now.toString());
  
  // For production, we might want to:
  // 1. Report to error tracking service
  // 2. Show a user-friendly notification
  // 3. Attempt recovery strategies
  
  // Check if this is a version mismatch issue (common in production)
  if (path.includes('-') && path.includes('.')) {
    console.warn('Possible version mismatch detected. User may need to refresh.');
    
    // Only show reload prompt once per session
    if (!sessionStorage.getItem('version-mismatch-shown')) {
      sessionStorage.setItem('version-mismatch-shown', 'true');
      
      // Delay the prompt to avoid interrupting initial load
      setTimeout(() => {
        if (window.confirm('A new version of the application is available. Would you like to reload?')) {
          window.location.reload();
        }
      }, 2000);
    }
  }
}

// Utility to preload critical assets with retry logic
export function preloadCriticalAssets(assets: string[]) {
  const preloadWithRetry = (href: string, retries = 2) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = href.endsWith('.css') ? 'style' : 'script';
    link.href = href;
    
    link.onerror = () => {
      if (retries > 0) {
        console.warn(`Retrying preload for: ${href}`);
        setTimeout(() => {
          link.remove();
          preloadWithRetry(href, retries - 1);
        }, 1000);
      } else {
        console.error(`Failed to preload after retries: ${href}`);
      }
    };
    
    document.head.appendChild(link);
  };
  
  assets.forEach(asset => preloadWithRetry(asset));
}